import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  keyframes,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ChangeEventHandler, FC, MouseEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import { cacheService, logService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import Activity, { ActivityMappedData } from "../activity";
import TableSkeleton from "../../Table/TableSkeleton";
import { Distinct, UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import CommentForm from "../../Misc/CommentForm/comment-form";
import { useSelector } from "react-redux";
import { GetObject } from "../../../Redux/Reducers/redux-slice-asset";
import moment from "moment";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import GlobalConst from "../../../Core/Utils/Constants/globals";

interface ActivityMainFrameProps {
  kind: ActivityKind;
  resourceId: number;
  depth?: number;
}

const ActivityMainFrame: FC<ActivityMainFrameProps> = (props) => {
  const { t } = useTranslation();
  const { kind, resourceId, depth } = props;
  const defaultDepth = 20;

  const user = cacheService.GetUserInfo();
  const fetchedObject = useSelector(GetObject);

  const [activities, setActivities] = useState<ActivityMappedData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const enqueueCustomSnackbar = useCustomSnackbar();

  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<
    "all" | "actions-needed" | "discussions" | "errors" | "mentions"
  >("all");
  const [isCommenting, setIsCommenting] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedType, setSelectedType] = useState("");

  const handleClearQueryClick: MouseEventHandler = () => {
    setQuery("");
  };

  const handleQueryChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setQuery(e.target.value);
  };

  const handleCategoryFilterChange = (
    _: React.MouseEvent,
    newValue: "all" | "actions-needed" | "discussions" | "errors" | "mentions"
  ) => {
    setCategoryFilter(newValue);
  };

  const handleCommentClick: MouseEventHandler = () => {
    setIsCommenting((prevState) => !prevState);
  };

  const handleCommentSend = (comment: string) => {
    logService
      .InsertActivityComment({
        message: comment,
        resId: resourceId,
        kind: kind,
        activityReplyId: null,
      })
      .then((response) => {
        enqueueCustomSnackbar(t("alert:success.commentHasBeenPosted"), "success");

        if (response.activity) {
          let mapped = logService.GetMappedActivity(response.activity);
          setActivities((acts) => [mapped, ...acts]);
        }
      });
  };

  useEffect(() => {
    logService.ListResourceActivities(kind, resourceId, 0, depth ? depth : defaultDepth).then((data) => {
      setActivities(data);
      setIsLoading(false);
    });
  }, [kind, resourceId]);

  return (
    <Stack direction="row" justifyContent="space-between">
      <FiltersWrapper sx={{ height: "100%", position: "sticky", top: 0, py: 2 }} mr={3}>
        <OutlinedInput
          size="small"
          sx={{ width: "100%", mb: 3 }}
          placeholder={t("common:search")}
          value={query}
          onChange={handleQueryChange}
          endAdornment={
            <InputAdornment position="end">
              {query && (
                <IconButton aria-label="Clear field" onClick={handleClearQueryClick} size="small" sx={{ mr: 1 }}>
                  <CloseIcon />
                </IconButton>
              )}
              <SearchIcon />
            </InputAdornment>
          }
        />
        <Select
          IconComponent={ExpandMoreRoundedIcon}
          value={selectedUser}
          size="small"
          sx={{ mb: 1, width: "100%" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          displayEmpty
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <MenuItem value="">
            <PersonOutlinedIcon fontSize="small" sx={{ color: "primary.text", mr: 1 }} />
            <Typography>{t("menu:item.allUsers")}</Typography>
          </MenuItem>
          {activities
            .map((act) => act.userName)
            .filter(Distinct)
            .map((user) => (
              <MenuItem value={user} key={user}>
                <PersonOutlinedIcon fontSize="small" sx={{ color: "primary.text", mr: 1 }} />
                <Typography>{user}</Typography>
              </MenuItem>
            ))}
        </Select>
        <Select
          IconComponent={ExpandMoreRoundedIcon}
          value={selectedType}
          size="small"
          sx={{ width: "100%" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          displayEmpty
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <MenuItem value="">
            <CategoryOutlinedIcon fontSize="small" sx={{ color: "primary.text", mr: 1 }} />
            <Typography>{t("menu:item.allActivities")}</Typography>
          </MenuItem>
          {activities
            .map((act) => act.headingAction + " " + act.headingLabel)
            .filter(Distinct)
            .map((type) => (
              <MenuItem value={type} key={type}>
                <PersonOutlinedIcon fontSize="small" sx={{ color: "primary.text", mr: 1 }} />
                <Typography>{type}</Typography>
              </MenuItem>
            ))}
        </Select>
        <FormGroup sx={{ mt: 3 }}>
          <FormControlLabel
            control={<Checkbox value={onlyMine} onClick={() => setOnlyMine((prev) => !prev)} />}
            label={t("field:label.showOnlyMyActivities")}
          />
        </FormGroup>
        {fetchedObject?.deleteInfos && (
          <Card sx={{ mt: 2, p: 1, backgroundColor: "backgroundAlpha.main" }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <ReportProblemIcon color="warning" />
              <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1 }}>
                {t("common:deleted")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t("common:deletedObjectWarning", {
                userName: fetchedObject.deleteInfos.userName,
                date: moment(fetchedObject.deleteInfos.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
              })}
            </Typography>
          </Card>
        )}
      </FiltersWrapper>
      <ActivitiesWrapper sx={{ height: "100%", py: 2 }} alignItems="center">
        <CategoryFiltersToggleButtonGroupWrapper sx={{ position: "sticky", top: 0 }}>
          <Stack direction="row" justifyContent="space-between">
            <CategoryFiltersToggleButtonGroup value={categoryFilter} exclusive onChange={handleCategoryFilterChange}>
              <ToggleButton color="primary" value="all">
                {t("common:all")}
              </ToggleButton>
              <ToggleButton color="primary" value="actions-needed">
                {t("button:actionsNeeded")}
              </ToggleButton>
              <ToggleButton color="primary" value="discussions">
                {t("common:discussions")}
              </ToggleButton>
              <ToggleButton color="primary" value="mentions">
                @{t("common:mentions")}
              </ToggleButton>
              <ToggleButton color="primary" value="errors">
                {t("common:errors")}
              </ToggleButton>
            </CategoryFiltersToggleButtonGroup>
            <Button
              onClick={handleCommentClick}
              size="small"
              variant={"outlined"}
              color={isCommenting ? "error" : "primary"}
              startIcon={isCommenting ? undefined : <AddIcon />}
              disabled={!UserHasAcl(AclTypes.aclActivityAdd)}
              sx={{ ml: 1 }}
            >
              {isCommenting ? t("common:cancel") : t("common:comment")}
            </Button>
          </Stack>
          {isCommenting && (
            <CommentWrapper>
              <CommentForm onSend={handleCommentSend} sx={{ mt: 2 }} />
              <Divider />
            </CommentWrapper>
          )}

          <Box mt={2}>
            {activities.length > 0 ? (
              activities
                .filter(
                  (x) =>
                    ((onlyMine && x.userId === user?.id) || !onlyMine) &&
                    ((selectedUser !== "" && x.userName === selectedUser) || selectedUser === "") &&
                    ((selectedType !== "" && selectedType === `${x.headingAction} ${x.headingLabel}`) ||
                      selectedType === "") &&
                    (query === "" ||
                      x.contextTitle?.includesAllWords(query) ||
                      x.contextSubtitle?.includesAllWords(query) ||
                      x.activityDetails?.includesAllWords(query) ||
                      x.contextLabel?.includesAllWords(query))
                )
                ?.map((activity) => <Activity {...activity} sx={{ mb: 2 }} key={activity.id} />)
            ) : (
              <>{isLoading ? <TableSkeleton /> : <Typography>{t("common:noActivities")}</Typography>}</>
            )}
          </Box>
        </CategoryFiltersToggleButtonGroupWrapper>
      </ActivitiesWrapper>
    </Stack>
  );
};

export default ActivityMainFrame;

const appearingAnimation = keyframes`0% {opacity: 0} 100% {opacity: 1}`;

const ActivitiesWrapper = styled(Stack)(() => ({
  marginLeft: "auto !important",
  marginRight: "auto !important",
  maxWidth: "600px",
}));

const FiltersWrapper = styled(Box)(({ theme }) => ({
  width: "240px",
}));

const CategoryFiltersToggleButtonGroupWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
}));

const CategoryFiltersToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  background: "transparent",
  ".MuiToggleButton-root": {
    borderRadius: "6px !important",
    border: `1px solid ${theme.palette.backgroundAlpha.border} !important`,
    background: "transparent",
    color: theme.palette.text.secondary,
    height: "32px",
    padding: `0 ${theme.spacing(1.5)}`,
    transition: theme.transitions.create(["color", "border-color", "background"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeOut,
    }),
    "&:not(:last-of-type)": {
      marginRight: theme.spacing(1),
    },
    "&:hover": {
      border: `1px solid ${theme.palette.backgroundAlpha.borderFocus} !important`,
      background: theme.palette.backgroundAlpha.hover,
    },
    "&.Mui-selected": {
      border: `1px solid ${theme.palette.primary.alphaPressed} !important`,
      background: theme.palette.primary.alpha,
      color: theme.palette.primary.main,
      boxShadow: "none",
    },
  },
}));

export const CommentWrapper = styled(Box)(() => ({
  opacity: 0,
  animation: `${appearingAnimation} 0.3s forwards`,
}));
